import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, Inbox, Loader2 } from 'lucide-react';
import client from './adminApi';

const Card = ({ label, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <div className="mt-3">{children}</div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get('/admin/dashboard').then((res) => setData(res.data)).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>;
  if (data.error) return <p role="alert" className="text-sm font-semibold text-red-700">Dashboard could not be loaded.</p>;

  const contentRows = [
    ['Pages', data.counts.pages, '/admin/content/pages'],
    ['Insights', data.counts.articles, '/admin/content/articles'],
    ['Services & Ecosystem', data.counts.listings, '/admin/content/listings'],
    ['Case Studies', data.counts.caseStudies, '/admin/content/case-studies'],
    ['FAQs', data.counts.faqs, '/admin/content/faqs'],
    ['Testimonials', data.counts.testimonials, '/admin/content/testimonials'],
    ['Timeline', data.counts.timeline, '/admin/content/timeline'],
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Website content, bookings and enquiries at a glance.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Card label="Content status" testid="dash-content">
          <ul className="space-y-2 text-sm">
            {contentRows.map(([label, c, href]) => (
              <li key={label}>
                <Link to={href} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50">
                  <span className="font-semibold text-navy-900">{label}</span>
                  <span className="text-xs text-slate-500">
                    {c.published} live{c.draft > 0 && ` · ${c.draft} draft`}{c.inReview > 0 && ` · ${c.inReview} in review`}{c.scheduled > 0 && ` · ${c.scheduled} scheduled`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        <Card label="Upcoming appointments" testid="dash-bookings">
          {data.upcomingBookings.length === 0 ? (
            <p className="text-sm text-slate-500">No upcoming bookings.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.upcomingBookings.map((b) => (
                <li key={b.bookingId} className="flex items-center gap-2.5">
                  <Calendar className="h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-navy-900">{b.customer.name} — {b.customer.businessName}</p>
                    <p className="text-xs text-slate-500">{new Date(b.startUtc).toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })} · {b.reference}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/bookings" className="mt-3 inline-block text-xs font-bold text-navy-900 underline-offset-2 hover:underline">Manage bookings</Link>
        </Card>

        <Card label="Recent enquiries" testid="dash-enquiries">
          {data.recentEnquiries.length === 0 ? (
            <p className="text-sm text-slate-500">No enquiries yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recentEnquiries.map((e) => (
                <li key={e.enquiryId} className="flex items-center gap-2.5">
                  <Inbox className="h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-navy-900">{e.name} — {e.businessName}</p>
                    <p className="text-xs text-slate-500">{e.source} · {new Date(e.submittedAt).toLocaleDateString('en-IE')}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/enquiries" className="mt-3 inline-block text-xs font-bold text-navy-900 underline-offset-2 hover:underline">View enquiries</Link>
        </Card>

        <Card label="Sync & email issues" testid="dash-sync">
          <p className="text-sm text-slate-600">
            Failed CRM syncs: <strong className="text-navy-900">{data.failedCrmSyncs}</strong>
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Failed email sends: <strong className="text-navy-900">{data.failedEmails}</strong>
          </p>
          <p className="mt-3 text-xs text-slate-400">CRM and email integrations are intentionally inactive; records queue with pending status.</p>
        </Card>

        <Card label="SEO attention needed" testid="dash-seo">
          {data.missingSeo.length === 0 ? (
            <p className="text-sm text-slate-500">All published content has titles and descriptions.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {data.missingSeo.map((m) => (
                <li key={`${m.resource}-${m.id}`}>
                  <Link to={`/admin/content/${m.resource}/${m.id}/edit`} className="flex items-center gap-2 font-semibold text-navy-900 hover:underline">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
                    {m.label} <span className="text-xs font-normal text-slate-400">({m.resource})</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card label="Recent activity" testid="dash-activity">
          <ul className="space-y-1.5 text-xs text-slate-600">
            {data.recentActivity.map((a, i) => (
              <li key={i} className="truncate">
                <span className="font-semibold text-navy-900">{a.user}</span> · {a.summary}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
